import React, { useState } from 'react'
import classNames from 'classnames'
import useBreakpoint from '#src/hooks/useBreakpoint'
import SidebarAwareGrid from '#src/components/SidebarAwareGrid'
import { Link } from 'react-router-dom'
import { Image, StarRating, Button, Grid, GridItem } from '@deal/components'
import LeftArrow from './left-arrow.svg'
import RightArrow from './right-arrow.svg'
import truncate from 'lodash/truncate'
import DepartmentExpertHeadline from '#src/components/DepartmentExpertHeadline'
import SliderWithScrollLock, {
  CustomArrowProps,
  SlickSlider
} from '#src/components/SliderWithScrollLock'
import styles from './styles.css'
import { FeaturedReviewsComponentProps } from '..'

const PrevArrow: React.SFC<CustomArrowProps> = ({ onClick }) => {
  return (
    <div className={styles.prevArrow} onClick={onClick}>
      <LeftArrow className={styles.arrowIcon} />
    </div>
  )
}

const NextArrow: React.SFC<CustomArrowProps> = ({ onClick }) => {
  return (
    <div className={styles.nextArrow} onClick={onClick}>
      <RightArrow className={styles.arrowIcon} />
    </div>
  )
}

interface ReviewDotsProps {
  dots: React.ReactNode[]
  currentSlideIndex: number
  sliderRef: React.RefObject<SlickSlider>
}

const ReviewDots: React.SFC<ReviewDotsProps> = ({ dots, currentSlideIndex, sliderRef }) => {
  // After 15 on desktop and 7 on mobile, we start to truncate the set
  const focusThreshold = useBreakpoint('md') ? 15 : 7
  // How far do we go each way from the current index
  const groupingThreshold = useBreakpoint('md') ? 7 : 3
  // Get the current dot in the set
  const currentDotInSetIndex = dots.findIndex((_dot, idx) => idx === currentSlideIndex)
  let dotSet = dots

  if (dots && dots.length > focusThreshold) {
    // Initialize the truncated dot set
    let truncatedDotSet = []

    if (currentDotInSetIndex < groupingThreshold) {
      // If it's less than the grouping threshold, just return the trimmed array until we pass the grouping threshold
      truncatedDotSet = dots.slice(0, focusThreshold)
    } else if (currentDotInSetIndex >= dots.length - groupingThreshold) {
      // If we're at the end of the array (past the focus threshold), then just grab the last focus threshold
      truncatedDotSet = dots.slice(dots.length - focusThreshold, dots.length)
    } else {
      const frontwardIndex = currentDotInSetIndex - groupingThreshold
      const backwardIndex = currentDotInSetIndex + 1

      truncatedDotSet.push(...dots.slice(frontwardIndex, frontwardIndex + groupingThreshold))
      truncatedDotSet.push(dots[currentDotInSetIndex])
      truncatedDotSet.push(...dots.slice(backwardIndex, backwardIndex + groupingThreshold))
    }

    dotSet = truncatedDotSet
  }

  return (
    <div>
      <div className={styles.dotsAndArrows}>
        <PrevArrow
          onClick={() => {
            if (sliderRef.current) {
              sliderRef.current.slickPrev()
            }
          }}
        />
        <div className={styles.dotContainer}>
          <ul className={styles.dots}>{dotSet}</ul>
        </div>
        <NextArrow
          onClick={() => {
            if (sliderRef.current) {
              sliderRef.current.slickNext()
            }
          }}
        />
      </div>
    </div>
  )
}

const Reviews: React.SFC<FeaturedReviewsComponentProps> = ({
  forceFullGrid = false,
  className,
  header,
  subHeader,
  disableLinking = false,
  showMoreReviewsButton = true,
  featuredReviews,
  actions
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const isLargerThanSm = useBreakpoint('sm')
  const isLargerThanLg = useBreakpoint('lg')

  let slidesToShow = 1.1
  if (isLargerThanLg) {
    slidesToShow = 2
  }

  let GridComponent = SidebarAwareGrid
  if (forceFullGrid) {
    GridComponent = Grid
  }

  const sliderRef: React.RefObject<SlickSlider> = React.createRef()
  const headerTruncateThreshold = isLargerThanSm ? 58 : 38
  const bodyTruncateTreshold = isLargerThanSm ? 345 : 235

  return (
    <div className={classNames(styles.wrapper, className)}>
      <GridComponent>
        <GridItem col={2}>
          {header && <div className={styles.header}>{header}</div>}
          {subHeader && <div className={styles.subHeader}>{subHeader}</div>}
        </GridItem>
      </GridComponent>

      <GridComponent className={styles.reviewCarousel}>
        <GridItem col={2}>
          <SliderWithScrollLock
            sliderRef={sliderRef}
            slidesToShow={slidesToShow}
            slidesToScroll={1}
            dots
            infinite={false}
            arrows={false}
            afterChange={currentSlide => {
              setCurrentSlideIndex(currentSlide)
            }}
            appendDots={dots => (
              <ReviewDots
                //@ts-ignore This is passing an array and is incorrectly typed
                dots={dots}
                currentSlideIndex={currentSlideIndex}
                sliderRef={sliderRef}
              />
            )}
          >
            {featuredReviews.map(review => {
              if (!review.expert.expertProfile || !review.expert.expertProfile.profilePhoto) {
                return null
              }

              return (
                <div style={{ marginLeft: '50px' }} key={review.id}>
                  <div className={styles.reviewCard}>
                    <div className={styles.reviewImage}>
                      <div>
                        {disableLinking ? (
                          <Image
                            src={review.expert.expertProfile.profilePhoto.url}
                            sizes="(min-width: 768px) 200px, 150px"
                            className={styles.image}
                          />
                        ) : (
                          <Link to={`/experts/${review.expert.vanityId || review.expert.id}`}>
                            <Image
                              src={review.expert.expertProfile.profilePhoto.url}
                              sizes="(min-width: 768px) 200px, 150px"
                              className={styles.image}
                            />
                          </Link>
                        )}
                      </div>
                      <div className={styles.reviewImageCopy}>
                        {disableLinking ? (
                          <div className={styles.expertName}>{review.expert.displayName}</div>
                        ) : (
                          <Link to={`/experts/${review.expert.vanityId || review.expert.id}`}>
                            <div className={styles.expertName}>{review.expert.displayName}</div>
                          </Link>
                        )}
                        <div className={styles.expertCategory}>
                          <DepartmentExpertHeadline
                            departmentType={review.expert.department.type}
                          />
                        </div>
                      </div>
                    </div>
                    <div className={styles.reviewDetails}>
                      <StarRating
                        rating={review.rating}
                        starDimension="24px"
                        starRatedColor="#f2962d"
                      />
                      {review.headline && (
                        <div className={styles.reviewHeadline}>
                          {truncate(review.headline, {
                            length: headerTruncateThreshold
                          })}
                        </div>
                      )}
                      {review.comments && (
                        <div className={styles.reviewBody}>
                          {truncate(review.comments, {
                            length: bodyTruncateTreshold
                          })}
                          {review.comments.length > bodyTruncateTreshold && !disableLinking && (
                            <Link
                              to={`/experts/${review.expert.vanityId ||
                                review.expert.id}?reviewId=${review.id}#reviews`}
                            >
                              <span className={styles.expand}>View more</span>
                            </Link>
                          )}
                        </div>
                      )}
                      {review.consumerName && (
                        <div className={styles.consumerName}>{review.consumerName}</div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </SliderWithScrollLock>
        </GridItem>
      </GridComponent>

      {(showMoreReviewsButton || actions) && (
        <GridComponent>
          <GridItem col={2} className={styles.actions}>
            {actions ? (
              <>{actions}</>
            ) : (
              <Link to="/reviews#reviews">
                <Button variant="contrast-outline">More reviews</Button>
              </Link>
            )}
          </GridItem>
        </GridComponent>
      )}
    </div>
  )
}

export default Reviews
